import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { FormattedMessage, useIntl, type IntlShape } from "react-intl";
import { ArrowRight, List, Loader2, Plus, Rows3 } from "lucide-react";
import { normalizeCampaignBrief, normalizeCampaignContentSlot } from "@runcheetah/shared";
import { toast } from "sonner";

import {
  CenteredLoader,
  EmptyCard,
  PageIntro,
  Skeleton,
  SkeletonTextBlock,
} from "@/components/common";
import { CreateCampaignModal } from "@/components/campaign/create-campaign-modal";
import { useAssetCreationGate } from "@/hooks/use-asset-creation-gate";
import { buildBrandPath, getUnavailableBrandPath, useRouteBrand } from "@/hooks/use-route-brand";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { Badge, Button, Card, CardContent } from "@runcheetah/ui";
import {
  CampaignAutopilotPausedBanner,
  CampaignAutopilotPausedEmptyState,
} from "./campaigns-autopilot-notice";
import {
  CAMPAIGN_FILTERS,
  filterCampaignsByBucket,
  getCampaignBucket,
  getCampaignFilterCount,
  getCampaignFilterLabel,
  getCampaignBucketLabel,
  getCampaignChannelLabels,
  getCampaignListMetrics,
  getActionablePendingCampaignGeneration,
  getPendingCampaignGenerationRefetchInterval,
  getCampaignTimelineBar,
  getCampaignTimelineMonthLabels,
  getCampaignTimelineRange,
  getCampaignTimelineTicks,
  getTodayMarkerPercent,
  type CampaignBucket,
  type CampaignFilter,
} from "./campaigns-state";

type CampaignListItem = {
  id: string;
  name: string;
  status: string;
  startDate: string;
  endDate: string;
  createdAt: string;
  summary: string;
  goals: string[];
  review: {
    totalSlots: number;
    readySlots: number;
    inFlightSlots: number;
    failedSlots: number;
    nextScheduledFor: string | null;
    featuredSlot: {
      scheduledPostId: string;
      concept: string;
      scheduledFor: string;
      status: string;
      previewAsset: {
        generatedAssetId: string;
        signedUrl: string;
        width: number | null;
        height: number | null;
      } | null;
    } | null;
  };
  scheduledPosts: Array<{
    id: string;
    scheduledFor: string;
    status: string;
    approvalStatus: string | null;
    platform: string;
    concept: string;
  }>;
};

type CampaignViewMode = "timeline" | "list";
type CampaignsLocationState = {
  queuedOnDemandCampaignGeneration?: boolean;
} | null;

const BUCKET_DOT_CLASSES: Record<CampaignBucket, string> = {
  needs_review: "bg-brand ring-2 ring-brand/30",
  active: "bg-brand",
  drafts: "bg-peach-foreground",
  complete: "bg-primary",
};

const BUCKET_BADGE_CLASSES: Record<CampaignBucket, string> = {
  needs_review: "border-brand/45 bg-brand/25 text-primary",
  active: "border-brand/30 bg-brand/15 text-primary",
  drafts: "border-peach/40 bg-peach text-peach-foreground",
  complete: "border-primary/15 bg-primary/10 text-primary",
};

const CHANNEL_CLASSES: Record<string, string> = {
  Instagram: "bg-pink-100 text-pink-600",
  Blog: "bg-emerald-100 text-emerald-700",
  Email: "bg-amber-100 text-amber-700",
};

const VIEW_OPTIONS: Array<{ value: CampaignViewMode; icon: typeof Rows3 }> = [
  { value: "timeline", icon: Rows3 },
  { value: "list", icon: List },
];

const PENDING_CAMPAIGN_GENERATION_REFETCH_INTERVAL_MS = 5_000;

const TIMELINE_LEGEND_BUCKETS: CampaignBucket[] = ["active", "needs_review", "drafts", "complete"];

const getRelativeCreatedLabel = (createdAt: string, now: Date, intl: IntlShape) => {
  const diffDays = Math.max(
    0,
    Math.floor((now.getTime() - new Date(createdAt).getTime()) / (24 * 60 * 60 * 1000)),
  );

  if (diffDays === 0) {
    return intl.formatMessage({ id: "campaigns.created.today", defaultMessage: "created today" });
  }

  if (diffDays === 1) {
    return intl.formatMessage({
      id: "campaigns.created.oneDayAgo",
      defaultMessage: "created 1 day ago",
    });
  }

  return intl.formatMessage(
    { id: "campaigns.created.daysAgo", defaultMessage: "created {count} days ago" },
    { count: diffDays },
  );
};

export default function CampaignsPage() {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedFilter, setSelectedFilter] = useState<CampaignFilter>("all");
  const [viewMode, setViewMode] = useState<CampaignViewMode>("timeline");
  const [createOpen, setCreateOpen] = useState(false);
  const [pollPendingCampaigns, setPollPendingCampaigns] = useState(false);
  const [optimisticPendingCampaignGeneration, setOptimisticPendingCampaignGeneration] =
    useState(false);
  const hadPendingCampaignGenerationRef = useRef(false);

  const { brand, brandQuery } = useRouteBrand();
  const assetCreationGate = useAssetCreationGate(brand?.id);

  const autopilotQuery = trpc.brand.getAutopilotSetting.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: !!brand?.id, staleTime: 0 },
  );
  const autopilotDisabled = autopilotQuery.data?.enabled === false;

  const generationCheckQuery = trpc.campaign.nextGenerationCheck.useQuery(
    { brandId: brand?.id ?? "" },
    {
      enabled: !!brand?.id,
      refetchOnWindowFocus: true,
      refetchInterval: (query) =>
        getPendingCampaignGenerationRefetchInterval({
          generationCheck: query.state.data ?? null,
          now: new Date(),
          intervalMs: PENDING_CAMPAIGN_GENERATION_REFETCH_INTERVAL_MS,
          autopilotEnabled: !autopilotDisabled,
        }),
    },
  );

  const campaignsQuery = trpc.campaign.list.useQuery(
    { brandId: brand?.id ?? "" },
    {
      enabled: !!brand?.id,
      refetchOnWindowFocus: true,
      refetchInterval: () =>
        pollPendingCampaigns ? PENDING_CAMPAIGN_GENERATION_REFETCH_INTERVAL_MS : false,
    },
  );

  const campaigns = useMemo(
    () =>
      (campaignsQuery.data ?? []).map<CampaignListItem>((campaign) => {
        const brief = normalizeCampaignBrief({
          briefJson: "brief" in campaign ? campaign.brief : undefined,
          goals: "goals" in campaign ? campaign.goals : undefined,
          audience: "audience" in campaign ? campaign.audience : undefined,
        });

        return {
          id: campaign.id,
          name: campaign.name,
          status: campaign.status,
          startDate: String(campaign.startDate),
          endDate: String(campaign.endDate),
          createdAt:
            "createdAt" in campaign && campaign.createdAt
              ? String(campaign.createdAt)
              : String(campaign.startDate),
          summary: brief.summary,
          goals: brief.goals.map((goal) => goal.label),
          review:
            "review" in campaign && campaign.review
              ? (campaign.review as CampaignListItem["review"])
              : {
                  totalSlots: (campaign.scheduledPosts ?? []).length,
                  readySlots: 0,
                  inFlightSlots: 0,
                  failedSlots: 0,
                  nextScheduledFor: null,
                  featuredSlot: null,
                },
          scheduledPosts: (campaign.scheduledPosts ?? []).map((post) => {
            const slot = normalizeCampaignContentSlot({
              slotJson: "slot" in post ? post.slot : undefined,
            });

            return {
              id: post.id,
              scheduledFor: String(post.scheduledFor),
              status: post.status,
              approvalStatus:
                "approvalStatus" in post && post.approvalStatus
                  ? String(post.approvalStatus)
                  : null,
              platform: slot.platform,
              concept: slot.concept,
            };
          }),
        };
      }),
    [campaignsQuery.data],
  );

  const overview = useMemo(() => {
    const futureSlots = campaigns
      .flatMap((campaign) =>
        campaign.scheduledPosts.map((slot) => ({
          campaignId: campaign.id,
          campaignName: campaign.name,
          ...slot,
        })),
      )
      .filter((slot) => new Date(slot.scheduledFor) >= new Date())
      .toSorted((a, b) => new Date(a.scheduledFor).getTime() - new Date(b.scheduledFor).getTime());

    const totalReady = campaigns.reduce((sum, campaign) => sum + campaign.review.readySlots, 0);
    const totalInFlight = campaigns.reduce(
      (sum, campaign) => sum + campaign.review.inFlightSlots,
      0,
    );
    const totalNeedsAttention = campaigns.reduce(
      (sum, campaign) => sum + campaign.review.failedSlots,
      0,
    );

    return {
      totalReady,
      totalInFlight,
      totalNeedsAttention,
      nextSlot: futureSlots[0] ?? null,
      futureSlots,
    };
  }, [campaigns]);

  const shortDateFmt = new Intl.DateTimeFormat(intl.locale, { month: "short", day: "numeric" });
  const longDateFmt = new Intl.DateTimeFormat(intl.locale, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  const monthDateFmt = new Intl.DateTimeFormat(intl.locale, { month: "short", year: "numeric" });
  const dayDateFmt = new Intl.DateTimeFormat(intl.locale, { day: "numeric" });

  const visibleCampaigns = useMemo(
    () => filterCampaignsByBucket(campaigns, selectedFilter),
    [campaigns, selectedFilter],
  );
  const timelineRange = useMemo(
    () => getCampaignTimelineRange({ campaigns: visibleCampaigns, now: new Date() }),
    [visibleCampaigns],
  );
  const todayMarker = getTodayMarkerPercent(timelineRange, new Date());
  const timelineTicks = getCampaignTimelineTicks(timelineRange);
  const timelineMonthLabels = getCampaignTimelineMonthLabels(timelineRange);
  const now = new Date();
  const actualPendingCampaignGeneration = getActionablePendingCampaignGeneration({
    generationCheck: generationCheckQuery.data ?? null,
    now,
    autopilotEnabled: !autopilotDisabled,
  });
  const pendingCampaignGeneration =
    actualPendingCampaignGeneration ??
    (optimisticPendingCampaignGeneration
      ? ({ mode: "on_demand", status: "queued" } as const)
      : null);
  const refetchCampaigns = campaignsQuery.refetch;
  const hasActualPendingCampaignGeneration = actualPendingCampaignGeneration !== null;
  const hasPendingCampaignGeneration = pendingCampaignGeneration !== null;

  const announceCampaignGenerationStarted = useCallback(() => {
    setOptimisticPendingCampaignGeneration(true);
    setPollPendingCampaigns(true);
    hadPendingCampaignGenerationRef.current = true;
    toast.success(
      intl.formatMessage({
        id: "campaigns.generationStartedToast",
        defaultMessage: "Campaign generation started.",
      }),
    );
  }, [intl]);

  useEffect(() => {
    if (hasActualPendingCampaignGeneration) {
      setOptimisticPendingCampaignGeneration(false);
    }
  }, [hasActualPendingCampaignGeneration]);

  useEffect(() => {
    const state = location.state as CampaignsLocationState;
    if (!state?.queuedOnDemandCampaignGeneration) {
      return;
    }

    announceCampaignGenerationStarted();
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [
    announceCampaignGenerationStarted,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);

  useEffect(() => {
    if (hasPendingCampaignGeneration) {
      hadPendingCampaignGenerationRef.current = true;
      setPollPendingCampaigns(true);
      return;
    }

    if (pollPendingCampaigns && !generationCheckQuery.isFetching) {
      void refetchCampaigns().finally(() => setPollPendingCampaigns(false));
    }
  }, [
    generationCheckQuery.isFetching,
    hasPendingCampaignGeneration,
    pollPendingCampaigns,
    refetchCampaigns,
  ]);

  useEffect(() => {
    if (
      !hadPendingCampaignGenerationRef.current ||
      hasPendingCampaignGeneration ||
      campaignsQuery.isFetching ||
      campaigns.length === 0
    ) {
      return;
    }

    hadPendingCampaignGenerationRef.current = false;
    toast.success(
      intl.formatMessage({
        id: "campaigns.generationReadyToast",
        defaultMessage: "Campaign generation finished. New content is ready to review.",
      }),
    );
  }, [campaigns.length, campaignsQuery.isFetching, hasPendingCampaignGeneration, intl]);

  if (brandQuery.isPending) {
    return <CenteredLoader />;
  }

  if (!brand || brand.status !== "scanned") {
    return <Navigate to={getUnavailableBrandPath(brand?.id)} replace />;
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-5 px-4 py-5 sm:px-6 lg:py-6">
      <PageIntro
        title={<FormattedMessage id="campaigns.title" defaultMessage="Campaigns" />}
        actions={
          <Button
            type="button"
            size="lg"
            onClick={() => assetCreationGate.requestAssetCreation(() => setCreateOpen(true))}
            disabled={assetCreationGate.gateState === "loading"}
          >
            <Plus className="size-5" aria-hidden="true" />
            <FormattedMessage id="campaigns.newCampaign" defaultMessage="New campaign" />
          </Button>
        }
      />

      {campaignsQuery.isPending ? (
        <div className="space-y-4" aria-hidden="true">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <Skeleton className="h-11 w-full max-w-xl rounded-2xl" />
            <Skeleton className="h-11 w-52 rounded-2xl" />
          </div>
          <Card className="overflow-hidden rounded-3xl border-border/70 p-0 shadow-none">
            <div className="grid grid-cols-[320px_minmax(620px,1fr)] border-b">
              <div className="p-5">
                <Skeleton className="h-4 w-28" />
              </div>
              <div className="border-l p-5">
                <Skeleton className="h-4 w-3/4" />
              </div>
            </div>
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="grid grid-cols-[320px_minmax(620px,1fr)] border-b last:border-b-0"
              >
                <div className="space-y-3 p-5">
                  <Skeleton className="h-5 w-3/4" />
                  <SkeletonTextBlock />
                </div>
                <div className="border-l p-5">
                  <Skeleton className="h-[72px] w-2/3" />
                </div>
              </div>
            ))}
          </Card>
        </div>
      ) : campaignsQuery.error ? (
        <Card>
          <CardContent className="py-10 text-sm text-destructive">
            <FormattedMessage
              id="campaigns.loadFailed"
              defaultMessage="Failed to load campaigns: {message}"
              values={{ message: campaignsQuery.error.message }}
            />
          </CardContent>
        </Card>
      ) : campaigns.length === 0 && pendingCampaignGeneration ? (
        <Card className="overflow-hidden rounded-3xl border-border/70 bg-card/95 shadow-sm">
          <CardContent
            className="flex min-h-52 flex-col items-center justify-center gap-5 px-6 py-12 text-center sm:min-h-56"
            role="status"
            aria-live="polite"
          >
            <div className="relative flex size-16 items-center justify-center rounded-3xl bg-brand/10 text-brand">
              <span
                className="absolute inset-0 animate-ping rounded-3xl bg-brand/10"
                aria-hidden="true"
              />
              <Loader2 className="relative size-7 animate-spin" aria-hidden="true" />
            </div>
            <div className="max-w-xl space-y-2">
              <h2 className="text-xl font-bold tracking-tight text-primary">
                {pendingCampaignGeneration.status === "processing" ? (
                  <FormattedMessage
                    id="campaigns.pending.processingTitle"
                    defaultMessage="Campaign generation in progress"
                  />
                ) : (
                  <FormattedMessage
                    id="campaigns.pending.queuedTitle"
                    defaultMessage="Campaign queued"
                  />
                )}
              </h2>
              <p className="text-sm leading-6 text-muted-foreground">
                {pendingCampaignGeneration.mode === "on_demand" ? (
                  <FormattedMessage
                    id="campaigns.pending.customMessage"
                    defaultMessage="Cheetah is preparing your custom campaign. It will appear here automatically once the plan is ready."
                  />
                ) : (
                  <FormattedMessage
                    id="campaigns.pending.strategyMessage"
                    defaultMessage="Cheetah is turning the active brand strategy into a campaign. It will appear here automatically once the plan is ready."
                  />
                )}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : campaigns.length === 0 ? (
        autopilotDisabled ? (
          <CampaignAutopilotPausedEmptyState brandId={brand.id} />
        ) : (
          <EmptyCard>
            <FormattedMessage
              id="campaigns.empty"
              defaultMessage="No campaigns yet. Create your first campaign to get started."
            />
          </EmptyCard>
        )
      ) : (
        <section className="space-y-7">
          {autopilotDisabled ? <CampaignAutopilotPausedBanner brandId={brand.id} /> : null}

          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="inline-flex w-full overflow-x-auto rounded-2xl bg-muted/70 p-1 shadow-xs sm:w-fit">
              {CAMPAIGN_FILTERS.map((filter) => (
                <button
                  key={filter}
                  type="button"
                  onClick={() => setSelectedFilter(filter)}
                  className={cn(
                    "flex shrink-0 items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-muted-foreground transition-colors",
                    selectedFilter === filter && "bg-card text-primary shadow-sm",
                  )}
                >
                  {getCampaignFilterLabel(filter)}
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-xs",
                      selectedFilter === filter
                        ? "bg-brand text-brand-foreground"
                        : "bg-background/60",
                    )}
                  >
                    {getCampaignFilterCount(campaigns, filter)}
                  </span>
                </button>
              ))}
            </div>

            <div className="inline-flex w-fit rounded-2xl bg-muted/70 p-1 shadow-xs">
              {VIEW_OPTIONS.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setViewMode(option.value)}
                    className={cn(
                      "flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-muted-foreground transition-colors",
                      viewMode === option.value && "bg-card text-primary shadow-sm",
                    )}
                  >
                    <Icon className="size-4" aria-hidden="true" />
                    {option.value === "timeline" ? (
                      <FormattedMessage id="campaigns.view.timeline" defaultMessage="Timeline" />
                    ) : (
                      <FormattedMessage id="campaigns.view.list" defaultMessage="List" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {visibleCampaigns.length === 0 && !pendingCampaignGeneration ? (
            <EmptyCard>
              <FormattedMessage
                id="campaigns.emptyView"
                defaultMessage="No campaigns in this view."
              />
            </EmptyCard>
          ) : viewMode === "timeline" ? (
            <Card className="overflow-hidden rounded-3xl border-border/70 bg-card/95 p-0 shadow-none">
              <div className="overflow-x-auto">
                <div className="min-w-[1080px]">
                  <div className="grid grid-cols-[360px_minmax(720px,1fr)] border-b bg-card">
                    <div className="px-6 py-5 text-sm font-bold uppercase tracking-wide text-muted-foreground">
                      <FormattedMessage
                        id="campaigns.timeline.campaignColumn"
                        defaultMessage="Campaign"
                      />
                    </div>
                    <div className="space-y-4 border-l px-0 py-3">
                      <div className="relative h-5 px-5 text-sm font-bold text-primary">
                        {timelineMonthLabels.map((label) => (
                          <span
                            key={label.date.toISOString()}
                            className="absolute top-0 whitespace-nowrap"
                            style={{ left: `${label.leftPercent}%` }}
                          >
                            {monthDateFmt.format(label.date)}
                          </span>
                        ))}
                      </div>
                      <div className="relative h-4 px-5 text-xs font-semibold text-muted-foreground">
                        {timelineTicks.map((tick) => (
                          <span
                            key={tick.date.toISOString()}
                            className="absolute top-0 -translate-x-1/2 first:translate-x-0 last:-translate-x-full"
                            style={{ left: `${tick.leftPercent}%` }}
                          >
                            {dayDateFmt.format(tick.date)}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {pendingCampaignGeneration ? (
                    <div
                      className="grid grid-cols-[360px_minmax(720px,1fr)] border-b bg-brand/5"
                      role="status"
                      aria-live="polite"
                    >
                      <div className="space-y-3 px-6 py-6">
                        <div className="flex items-start gap-3">
                          <span className="relative mt-1.5 size-2.5 shrink-0 rounded-full bg-brand ring-4 ring-brand/20">
                            <span
                              className="absolute inset-0 animate-ping rounded-full bg-brand/40"
                              aria-hidden="true"
                            />
                          </span>
                          <div className="min-w-0 space-y-2">
                            <h2 className="text-base font-bold text-primary">
                              {pendingCampaignGeneration.status === "processing" ? (
                                <FormattedMessage
                                  id="campaigns.pending.processingTitle"
                                  defaultMessage="Campaign generation in progress"
                                />
                              ) : (
                                <FormattedMessage
                                  id="campaigns.pending.queuedTitle"
                                  defaultMessage="Campaign queued"
                                />
                              )}
                            </h2>
                            <p className="max-w-sm text-sm leading-5 text-muted-foreground">
                              {pendingCampaignGeneration.mode === "on_demand" ? (
                                <FormattedMessage
                                  id="campaigns.pending.customMessage"
                                  defaultMessage="Cheetah is preparing your custom campaign. It will appear here automatically once the plan is ready."
                                />
                              ) : (
                                <FormattedMessage
                                  id="campaigns.pending.strategyMessage"
                                  defaultMessage="Cheetah is turning the active brand strategy into a campaign. It will appear here automatically once the plan is ready."
                                />
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2 pl-5" aria-hidden="true">
                          <Skeleton className="h-5 w-16 rounded-full" />
                          <Skeleton className="h-5 w-20 rounded-full" />
                        </div>
                      </div>
                      <div className="relative border-l bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px)] bg-[length:20%_100%] px-5 py-6">
                        {todayMarker !== null ? (
                          <div
                            className="absolute inset-y-0 z-10 w-0.5 bg-brand"
                            style={{ left: `${todayMarker}%` }}
                            aria-hidden="true"
                          />
                        ) : null}
                        <div className="relative h-[74px] w-2/5 rounded-xl border-l-4 border-brand bg-brand/15 px-4 py-4 shadow-sm">
                          <Skeleton className="h-4 w-28 bg-brand/20" />
                          <Skeleton className="mt-3 h-4 w-52 bg-brand/20" />
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {visibleCampaigns.map((campaign) => {
                    const bucket = getCampaignBucket(campaign);
                    const bar = getCampaignTimelineBar(campaign, timelineRange);
                    const metrics = getCampaignListMetrics(campaign);
                    const channels = getCampaignChannelLabels(campaign);

                    return (
                      <Link
                        key={campaign.id}
                        to={buildBrandPath(brand.id, `/campaigns/${campaign.id}`)}
                        className="group grid grid-cols-[360px_minmax(720px,1fr)] border-b last:border-b-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-inset"
                      >
                        <div className="space-y-3 px-6 py-6">
                          <div className="flex items-start gap-2">
                            <span
                              className={cn(
                                "mt-1.5 size-2.5 rounded-full",
                                BUCKET_DOT_CLASSES[bucket],
                              )}
                            />
                            <div className="min-w-0">
                              <h2 className="line-clamp-2 text-base font-bold text-primary">
                                {campaign.name}
                              </h2>
                              <div className="mt-2 text-sm text-muted-foreground">
                                <span className="font-semibold text-foreground">
                                  {getCampaignBucketLabel(bucket)}
                                </span>{" "}
                                |{" "}
                                <FormattedMessage
                                  id="campaigns.timeline.metrics"
                                  defaultMessage="{assets} assets | {pending} to review"
                                  values={{ assets: metrics.assets, pending: metrics.pending }}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 pl-5">
                            {channels.slice(0, 3).map((channel) => (
                              <Badge
                                key={channel}
                                shape="pill"
                                variant="secondary"
                                className={cn("border-0 text-[11px]", CHANNEL_CLASSES[channel])}
                              >
                                {channel}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div className="relative border-l bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px)] bg-[length:20%_100%] px-5 py-6">
                          {todayMarker !== null ? (
                            <div
                              className="absolute inset-y-0 z-10 w-0.5 bg-brand"
                              style={{ left: `${todayMarker}%` }}
                              aria-hidden="true"
                            />
                          ) : null}
                          <div
                            className={cn(
                              "relative h-[74px] rounded-xl border-l-4 px-4 py-4 shadow-sm transition-[box-shadow,ring-color] group-hover:shadow-md group-hover:ring-1 group-hover:ring-primary/12",
                              bucket === "needs_review" &&
                                "border-brand bg-brand/25 ring-1 ring-brand/35",
                              bucket === "active" && "border-brand bg-brand/20",
                              bucket === "drafts" && "border-peach-foreground/35 bg-peach/80",
                              bucket === "complete" && "border-primary bg-primary/10",
                            )}
                            style={{
                              marginLeft: `${bar.leftPercent}%`,
                              width: `${bar.widthPercent}%`,
                            }}
                            aria-label={intl.formatMessage(
                              {
                                id: "campaigns.timeline.barLabel",
                                defaultMessage: "{name}, {startDate} to {endDate}, {bucket}",
                              },
                              {
                                name: campaign.name,
                                startDate: shortDateFmt.format(new Date(campaign.startDate)),
                                endDate: shortDateFmt.format(new Date(campaign.endDate)),
                                bucket: getCampaignBucketLabel(bucket),
                              },
                            )}
                          >
                            <div className="truncate text-sm font-bold text-primary">
                              {shortDateFmt.format(new Date(campaign.startDate))} -{" "}
                              {shortDateFmt.format(new Date(campaign.endDate))}
                            </div>
                            <div className="truncate text-sm text-primary/80">
                              {campaign.goals[0] ?? campaign.summary}
                            </div>
                          </div>
                        </div>
                      </Link>
                    );
                  })}

                  <div className="flex items-center justify-between border-t px-6 py-4 text-sm text-muted-foreground">
                    <div className="flex flex-wrap gap-5">
                      {TIMELINE_LEGEND_BUCKETS.map((bucket) => (
                        <span key={bucket} className="inline-flex items-center gap-2">
                          <span
                            className={cn("size-2.5 rounded-full", BUCKET_DOT_CLASSES[bucket])}
                          />
                          {getCampaignBucketLabel(bucket)}
                        </span>
                      ))}
                    </div>
                    {todayMarker !== null ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="h-4 w-0.5 bg-brand" />{" "}
                        <FormattedMessage id="common.today" defaultMessage="Today" />
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            </Card>
          ) : (
            <div className="space-y-4">
              {pendingCampaignGeneration ? (
                <Card
                  className="overflow-hidden rounded-3xl border-brand/35 bg-brand/5 shadow-sm"
                  role="status"
                  aria-live="polite"
                >
                  <CardContent className="grid gap-5 p-5 lg:grid-cols-[minmax(0,1fr)_420px_auto] lg:items-center lg:p-6">
                    <div className="min-w-0 space-y-4">
                      <div className="flex items-start gap-3">
                        <span className="relative mt-1.5 size-2.5 shrink-0 rounded-full bg-brand ring-4 ring-brand/20">
                          <span
                            className="absolute inset-0 animate-ping rounded-full bg-brand/40"
                            aria-hidden="true"
                          />
                        </span>
                        <div className="min-w-0 space-y-2">
                          <h2 className="text-xl font-bold tracking-tight text-primary">
                            {pendingCampaignGeneration.status === "processing" ? (
                              <FormattedMessage
                                id="campaigns.pending.processingTitle"
                                defaultMessage="Campaign generation in progress"
                              />
                            ) : (
                              <FormattedMessage
                                id="campaigns.pending.queuedTitle"
                                defaultMessage="Campaign queued"
                              />
                            )}
                          </h2>
                          <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
                            {pendingCampaignGeneration.mode === "on_demand" ? (
                              <FormattedMessage
                                id="campaigns.pending.customMessage"
                                defaultMessage="Cheetah is preparing your custom campaign. It will appear here automatically once the plan is ready."
                              />
                            ) : (
                              <FormattedMessage
                                id="campaigns.pending.strategyMessage"
                                defaultMessage="Cheetah is turning the active brand strategy into a campaign. It will appear here automatically once the plan is ready."
                              />
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 pl-5" aria-hidden="true">
                        <Skeleton className="h-5 w-16 rounded-full bg-brand/20" />
                        <Skeleton className="h-5 w-20 rounded-full bg-brand/20" />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3 text-center" aria-hidden="true">
                      {Array.from({ length: 3 }).map((_, index) => (
                        <div key={index} className="space-y-2">
                          <Skeleton className="mx-auto h-6 w-10 bg-brand/20" />
                          <Skeleton className="mx-auto h-3 w-16 bg-brand/20" />
                        </div>
                      ))}
                    </div>

                    <Loader2 className="hidden size-6 animate-spin justify-self-end text-brand lg:block" />
                  </CardContent>
                </Card>
              ) : null}

              {visibleCampaigns.map((campaign) => {
                const bucket = getCampaignBucket(campaign);
                const metrics = getCampaignListMetrics(campaign);
                const channels = getCampaignChannelLabels(campaign);

                return (
                  <Link
                    key={campaign.id}
                    to={buildBrandPath(brand.id, `/campaigns/${campaign.id}`)}
                    className="group block rounded-3xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-2"
                  >
                    <Card
                      className={cn(
                        "rounded-3xl border-border/70 bg-card/95 shadow-sm transition-[border-color,box-shadow,ring-color] group-hover:border-accent/30 group-hover:shadow-md group-hover:ring-1 group-hover:ring-accent/12",
                        bucket === "needs_review" &&
                          "border-brand/45 bg-brand/5 ring-1 ring-brand/25",
                      )}
                    >
                      <CardContent className="grid gap-5 p-5 lg:grid-cols-[minmax(0,1fr)_420px_auto] lg:items-center lg:p-6">
                        <div className="min-w-0 space-y-4">
                          <div className="flex flex-wrap items-center gap-3">
                            <h2 className="text-xl font-bold tracking-tight text-primary">
                              {campaign.name}
                            </h2>
                            <Badge
                              shape="pill"
                              variant="outline"
                              className={cn("border text-xs", BUCKET_BADGE_CLASSES[bucket])}
                            >
                              {getCampaignBucketLabel(bucket)}
                            </Badge>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {campaign.goals[0] ??
                              intl.formatMessage({
                                id: "campaigns.fallbackName",
                                defaultMessage: "Campaign",
                              })}{" "}
                            | {shortDateFmt.format(new Date(campaign.startDate))} -{" "}
                            {shortDateFmt.format(new Date(campaign.endDate))} |{" "}
                            {getRelativeCreatedLabel(campaign.createdAt, now, intl)}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {channels.slice(0, 3).map((channel) => (
                              <Badge
                                key={channel}
                                shape="pill"
                                variant="secondary"
                                className={cn("border-0 text-[11px]", CHANNEL_CLASSES[channel])}
                              >
                                {channel}
                              </Badge>
                            ))}
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-3 text-center">
                          <div>
                            <div className="text-xl font-bold tabular-nums text-primary">
                              {metrics.assets}
                            </div>
                            <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                              <FormattedMessage
                                id="campaigns.metrics.assets"
                                defaultMessage="Assets"
                              />
                            </div>
                          </div>
                          <div>
                            <div className="text-xl font-bold tabular-nums text-amber-700">
                              {metrics.pending}
                            </div>
                            <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                              <FormattedMessage
                                id="campaigns.metrics.pending"
                                defaultMessage="Pending"
                              />
                            </div>
                          </div>
                          <div>
                            <div className="text-xl font-bold tabular-nums text-emerald-700">
                              {metrics.approved}
                            </div>
                            <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                              <FormattedMessage
                                id="campaigns.metrics.approved"
                                defaultMessage="Approved"
                              />
                            </div>
                          </div>
                        </div>

                        <ArrowRight
                          className="hidden size-6 justify-self-end text-muted-foreground transition-colors group-hover:text-accent lg:block"
                          aria-hidden="true"
                        />
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}

          <div className="sr-only" aria-live="polite">
            <FormattedMessage
              id="campaigns.overviewStatus"
              defaultMessage="{ready} slots ready to review, {inFlight} in progress, and {attention} needing attention."
              values={{
                ready: overview.totalReady,
                inFlight: overview.totalInFlight,
                attention: overview.totalNeedsAttention,
              }}
            />
            {overview.nextSlot ? (
              <FormattedMessage
                id="campaigns.nextSlotStatus"
                defaultMessage=" Next slot {date}."
                values={{ date: longDateFmt.format(new Date(overview.nextSlot.scheduledFor)) }}
              />
            ) : null}
          </div>
        </section>
      )}

      <CreateCampaignModal
        brandId={brand.id}
        brandName={brand.sourceDomain}
        open={createOpen}
        onOpenChange={setCreateOpen}
        onQueued={announceCampaignGenerationStarted}
        onUpgradeRequired={assetCreationGate.openUpgradeModal}
        upgradeLabel={assetCreationGate.upgradeLabel}
      />
      {assetCreationGate.upgradeModal}
    </div>
  );
}
